import { IonList, IonItem, IonButton, IonIcon, IonBadge } from '@ionic/react';
import { eyeOutline, trophy } from 'ionicons/icons';
import { TeamMember, TeamAgent } from '../../types/teamSummary';
import { ProfileStatus } from '../../types/profile';
import { formatLastLogin } from '../../utils/teamData';
import { RowActionMenu } from './RowActionMenu';
import { AgentActionMenu } from './AgentActionMenu';
import { getScoreColor } from '../mystery-shop/utils';

interface TeamMemberListProps {
  users: TeamMember[];
  agents?: TeamAgent[];
  shop?: boolean;
  onViewBadges: (event: React.MouseEvent, member: TeamMember) => void;
  onViewCertifications: (event: React.MouseEvent, member: TeamMember) => void;
  onRemove: (event: React.MouseEvent, member: TeamMember) => void;
  onSuspend: (event: React.MouseEvent, member: TeamMember) => void;
  onActivate: (event: React.MouseEvent, member: TeamMember) => void;
  onViewActivityLog: (event: React.MouseEvent, member: TeamMember) => void;
  onViewCourses: (event: React.MouseEvent, member: TeamMember) => void;
  onAssociateAgentToUser?: (event: React.MouseEvent, agent: TeamAgent) => void;
}

export function TeamMemberList({
  users,
  agents = [],
  shop,
  onViewBadges,
  onViewCertifications,
  onRemove,
  onSuspend,
  onActivate,
  onViewActivityLog,
  onViewCourses,
  onAssociateAgentToUser
}: TeamMemberListProps) {
  return (
    <IonList>
      {/* Desktop Column Headers - Hidden on mobile */}
      <div className="hidden md:flex flex-col md:flex-row gap-4 w-full">
        <div className="flex-1">User</div>
        <div className="flex-1">Last Login</div>
        <div className="flex-1">Courses Completed</div>
        <div className="flex-1">Progress%</div>
        <div className="flex-1">Badges Earned</div>
        <div className="w-12"></div>
      </div>

      {users.map((member) => {
        return (
          <IonItem key={member.id}>
            <div className="flex flex-col md:flex-row gap-3 md:gap-4 w-full relative pt-12 md:pt-0">
              {/* Mobile: Actions in top right */}
              <div className="absolute top-2 right-0 flex items-center gap-3 md:relative md:top-auto md:right-auto md:w-12 md:justify-center">
                <IonIcon 
                  icon={eyeOutline} 
                  className="text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
                  style={{ fontSize: '1.25rem' }}
                  onClick={(e) => onViewCourses(e as any, member)}
                />
                <RowActionMenu
                  member={member}
                  onRemove={onRemove}
                  onSuspend={onSuspend}
                  onActivate={onActivate}
                  onViewActivityLog={onViewActivityLog}
                />
              </div>

              {/* Mobile: Stacked with labels, Desktop: Row layout */}
              <div className="flex-1 md:flex-1 flex justify-between items-center md:block">
                <div className="flex items-center gap-2">
                  <span className="md:hidden font-medium text-gray-700">User: </span>
                  <span className="md:hidden">{member.displayName}</span>
                  <span className="hidden md:inline">{member.displayName}</span>
                  {member.status === ProfileStatus.Suspended ? (
                    <IonBadge
                      className="px-2 py-1 text-xs font-semibold"
                      style={{
                        '--background': '#D44D5C',
                        '--color': '#000000'
                      } as React.CSSProperties}
                    >
                      Suspended
                    </IonBadge>
                  ) : (
                    <IonBadge
                      color={member.status === ProfileStatus.Pending ? 'warning' : 'success'}
                      className="px-2 py-1 text-xs font-semibold"
                    >
                      {member.status === ProfileStatus.Pending ? 'Pending' : 'Active'}
                    </IonBadge>
                  )}
                </div>
              </div>

              <div className="flex-1 md:flex-1 flex justify-between items-center md:block">
                <span className="md:hidden font-medium text-gray-700">Last Login: </span>
                <span className="md:hidden">{formatLastLogin(member.lastSeen)}</span>
                <span className="hidden md:inline">{formatLastLogin(member.lastSeen)}</span>
              </div>

              <div className="flex-1 md:flex-1 flex justify-between items-center md:block">
                <span className="md:hidden font-medium text-gray-700">Courses Completed: </span>
                <IonBadge color="primary" className="px-3 py-1.5 text-sm font-semibold">
                  {member.totalCoursesCompleted}
                </IonBadge>
              </div>

              <div className="flex-1 md:flex-1 flex justify-between items-center md:block">
                <span className="md:hidden font-medium text-gray-700">Progress%: </span>
                <span className="text-[#231F20] text-base font-semibold">
                  {member.progress ?? 0}%
                </span>
              </div>

              <div className="flex-1 md:flex-1 flex justify-between items-center md:block">
                <span className="md:hidden font-medium text-gray-700">Badges Earned: </span>
                <IonButton
                  fill="clear"
                  size="small"
                  onClick={(e) => onViewBadges(e as any, member)}
                >
                  <IonIcon icon={trophy} slot="start" />
                  {member.badges?.length || 0}
                </IonButton>
              </div>

              {shop && (
                <div className="flex-1 md:flex-1 flex justify-between items-center md:block">
                  <span className="md:hidden font-medium text-gray-700">Calls: </span>
                  <span className="text-gray-600 text-sm">{member.agentPerformance?.callCount ?? 0}</span>
                </div>
              )}

              {shop && (
                <div className="flex-1 md:flex-1 flex justify-between items-center md:block">
                  <span className="md:hidden font-medium text-gray-700">Avg Score: </span>
                  {member.agentPerformance?.avgScore != null ? (
                    <span className="font-semibold" style={{ color: getScoreColor(member.agentPerformance.avgScore) }}>
                      {member.agentPerformance.avgScore.toFixed(0)}
                    </span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </div>
              )}

              {shop && (
                <div className="flex-1 md:flex-1 flex justify-between items-center md:block">
                  <span className="md:hidden font-medium text-gray-700">Trend: </span>
                  {member.agentPerformance?.trend != null ? (
                    <span className={member.agentPerformance.trend >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {member.agentPerformance.trend >= 0 ? '↑' : '↓'} {Math.abs(member.agentPerformance.trend).toFixed(0)}
                    </span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </div>
              )}
            </div>
          </IonItem>
        );
      })}

      {/* Agent rows (standalone agents, performance data only) */}
      {agents.map((agent) => (
        <IonItem key={agent.id}>
          <div className="flex flex-col gap-3 w-full py-2 relative pt-10">
            <div className="absolute top-2 right-0">
              <AgentActionMenu
                onAssociateToUser={(e) => onAssociateAgentToUser?.(e, agent)}
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="md:hidden font-medium text-gray-700">Agent: </span>
              <span>{agent.displayName}</span>
            </div>

            {shop && (
              <div className="flex justify-between items-center">
                <span className="font-medium text-gray-700">Calls: </span>
                <span className="text-gray-600 text-sm">{agent.agentPerformance?.callCount ?? 0}</span>
              </div>
            )}

            {shop && (
              <div className="flex justify-between items-center">
                <span className="font-medium text-gray-700">Avg Score: </span>
                {agent.agentPerformance?.avgScore != null ? (
                  <span className="font-semibold" style={{ color: getScoreColor(agent.agentPerformance.avgScore) }}>
                    {agent.agentPerformance.avgScore.toFixed(0)}
                  </span>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </div>
            )}

            {shop && (
              <div className="flex justify-between items-center">
                <span className="font-medium text-gray-700">Trend: </span>
                {agent.agentPerformance?.trend != null ? (
                  <span className={agent.agentPerformance.trend >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {agent.agentPerformance.trend >= 0 ? '↑' : '↓'} {Math.abs(agent.agentPerformance.trend).toFixed(0)}
                  </span>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </div>
            )}
          </div>
        </IonItem>
      ))}
    </IonList>
  );
}

